import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Clock, Image as ImageIcon, KeyRound, Loader2, Mail, Save, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../../components/PageHeader";
import { PasswordStrength } from "../../components/PasswordStrength";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { api, apiError } from "../../lib/api";

const pwSchema = z
  .object({
    current_password: z.string().min(1, "Enter your current password"),
    new_password: z
      .string()
      .min(8, "At least 8 characters")
      .regex(/[A-Z]/, "Add an uppercase letter")
      .regex(/[a-z]/, "Add a lowercase letter")
      .regex(/[0-9]/, "Add a number")
      .regex(/[^A-Za-z0-9]/, "Add a special character"),
  })
  .strict();

const Info = ({ icon: Icon, title, children }) => (
  <div className="flex gap-3 border-b border-border py-3 last:border-0">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
    <div>
      <p className="text-sm font-medium">{title}</p>
      <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{children}</p>
    </div>
  </div>
);

export default function AdminSettings() {
  const { user, refresh } = useAuth();
  const { theme } = useTheme();
  const [profile, setProfile] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
  });
  const [savingProfile, setSavingProfile] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(pwSchema),
    defaultValues: { current_password: "", new_password: "" },
  });
  const newPassword = watch("new_password");

  const saveProfile = async (event) => {
    event.preventDefault();
    setSavingProfile(true);
    try {
      await api.put("/api/admin/profile", profile);
      await refresh();
      toast.success("Admin profile updated");
    } catch (error) {
      toast.error(apiError(error, "Could not update the profile"));
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (values) => {
    try {
      await api.post("/api/auth/change-password", values);
      toast.success("Admin password changed");
      reset();
    } catch (error) {
      toast.error(apiError(error, "Could not change the password"));
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Settings" subtitle="Your admin account, appearance and platform information." />

      <div className="grid gap-5 lg:grid-cols-2">
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Admin profile</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={saveProfile} className="space-y-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={profile.name}
                  onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
                  data-testid="admin-settings-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
                  data-testid="admin-settings-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={profile.phone}
                  onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))}
                  data-testid="admin-settings-phone"
                />
              </div>
              <Button type="submit" disabled={savingProfile} data-testid="admin-settings-save-profile">
                {savingProfile ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                Save profile
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Change admin password</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(changePassword)} className="space-y-4" noValidate>
              <div className="space-y-2">
                <Label>Current password</Label>
                <Input
                  type="password"
                  autoComplete="current-password"
                  {...register("current_password")}
                  data-testid="admin-settings-current-password"
                />
                {errors.current_password ? (
                  <p className="text-xs text-destructive">{errors.current_password.message}</p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label>New password</Label>
                <Input
                  type="password"
                  autoComplete="new-password"
                  {...register("new_password")}
                  data-testid="admin-settings-new-password"
                />
                {errors.new_password ? (
                  <p className="text-xs text-destructive">{errors.new_password.message}</p>
                ) : null}
                <PasswordStrength value={newPassword} />
              </div>
              <Button type="submit" disabled={isSubmitting} data-testid="admin-settings-change-password">
                {isSubmitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <KeyRound className="mr-2 h-4 w-4" />
                )}
                Update password
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Platform information</CardTitle>
        </CardHeader>
        <CardContent>
          <Info icon={ShieldCheck} title="Security">
            All admin actions are recorded in the activity log. Client accounts are read-only for every
            financial value, badge and level.
          </Info>
          <Info icon={Clock} title="Session timeout">
            Sessions expire automatically and users are returned to the sign-in page with a notice.
            Login and password-reset attempts are rate limited per IP address.
          </Info>
          <Info icon={Mail} title="Email delivery">
            Password reset and notification emails are delivered through Resend. Until a sending domain
            is verified, the provider only delivers to the account owner address — verify
            invextiing.com in the Resend dashboard to email clients.
          </Info>
          <Info icon={ImageIcon} title="Replacing the logo">
            Overwrite the file at frontend/public/logo.webp with your own artwork. No code changes are
            required; the header, sign-in pages and emails pick it up automatically.
          </Info>
          <Info icon={ShieldCheck} title="Appearance">
            The interface is currently in {theme} mode. Each user&apos;s preference is stored on their own
            device and can be switched from the header at any time.
          </Info>
        </CardContent>
      </Card>
    </div>
  );
}
